import { useCallback, useEffect, useMemo, useState } from 'react';

import type { UserData } from '@genially/ui';
import { LogRocketAnonymousIntegration } from '@genially/ui/build/business/support/integrations/LogRocket/LogRocketAnonymousIntegration';
import { LogRocketEvent } from '@genially/ui/build/business/support/integrations/LogRocket/LogRocketEvent';
import { LogRocketIntegration } from '@genially/ui/build/business/support/integrations/LogRocket/LogRocketIntegration';
import {
  hasOnetrustPermissionToLoadLogRocket,
  loadLogRocketIntegration,
} from '@genially/ui/build/business/support/integrations/LogRocket/loadLogRocketIntegration';
import { TrackService } from '@genially/ui/build/business/support/services/track/TrackService';
import config from '@genially/ui/build/config/config';

import { useAuthenticatedUser } from '../../components/AuthenticatedUserProvider/AuthenticatedUserProvider.context';
import { getIsLoadTestFromUrl } from '../utils/getIsLoadTestFromUrl';

let initialized = false;
let initializationPromise: Promise<void>;

const { isProduction, urlView, playUrl } = config;

const logRocketService = new TrackService();

type SentEvent = {
  name: string;
  sampled: boolean;
  tracked: boolean;
};

let CachedTrackedEvents: Array<SentEvent> = [];

export const useLogRocketService = () => {
  const { authenticatedUser } = useAuthenticatedUser();

  const [trackedEvents, setTrackedEvents] = useState(CachedTrackedEvents);

  useEffect(() => {
    CachedTrackedEvents = trackedEvents;
  }, [trackedEvents]);

  const init = useCallback(() => {
    const isLoadTest = getIsLoadTestFromUrl();

    if (isLoadTest) {
      return Promise.resolve();
    }

    if (initializationPromise) return initializationPromise;

    initializationPromise = new Promise<void>(resolve => {
      if (!isProduction) {
        resolve();
      }

      if (initialized) {
        resolve();
      }

      if (!hasOnetrustPermissionToLoadLogRocket()) {
        resolve();
      }

      initialized = true;

      const startLogRocket = (lrInstance: unknown) => {
        if (authenticatedUser) {
          const userData = {
            id: authenticatedUser.id,
            firstName: authenticatedUser.username,
            lastName: '',
            userName: authenticatedUser.username,
            Sector: authenticatedUser.sector,
            Role: authenticatedUser.role,
          } as UserData;

          logRocketService.start(new LogRocketIntegration(userData));

          return;
        }

        logRocketService.start(new LogRocketAnonymousIntegration(lrInstance));
      };

      loadLogRocketIntegration(
        {
          appId: 'genially-z2e5p/play-ioyop',
          releaseVersion: import.meta.env.VITE_APP_RELEASE_VERSION ?? 'unknown',
          childDomains: [urlView, playUrl],
        },
        true,
      ).then(lrInstance => {
        if (lrInstance) {
          startLogRocket(lrInstance);
          resolve();
        }
      });
    });

    return initializationPromise;
  }, [authenticatedUser]);

  const trackWithSampling = useCallback((eventName: string, samplingRate: number) => {
    const event = {
      name: eventName,
      sampled: true,
      tracked: false,
    };

    if (!isProduction) {
      console.debug(`[LogRocket] Track event: ${eventName} - Skipped (not production)`);

      return event;
    }

    const tracked = logRocketService.trackEventWithSampling(
      new LogRocketEvent({
        name: eventName,
      }),
      samplingRate,
    );

    event.tracked = tracked;

    console.debug(`[LogRocket] Track event: ${eventName} - Tracked: ${tracked}`);

    setTrackedEvents(prev => {
      prev.push(event);

      return prev;
    });

    return event;
  }, []);

  const trackWithSamplingOnce = useCallback(
    (eventName: string, samplingRate: number) => {
      const isAlreadyTracked = trackedEvents.find(e => e.name === eventName);

      if (isAlreadyTracked) {
        return isAlreadyTracked.tracked;
      }

      return trackWithSampling(eventName, samplingRate).tracked;
    },
    [trackWithSampling, trackedEvents],
  );

  return {
    init,
    trackWithSampling,
    trackWithSamplingOnce,
    trackedEvents: useMemo(
      () => trackedEvents.filter(e => e.tracked).map(e => e.name),
      [trackedEvents],
    ),
  };
};
